import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { AlcoholicDrinkType } from '@products/interfaces/alcoholic-drink-type';
import { NonAlcoholicDrinkType } from '@products/interfaces/non-alcoholic-drink-type';
import { OtherAlcoholType } from '@products/interfaces/other-alcohol-type';
import { OtherSpiritType } from '@products/interfaces/other-spirit-type';
import { SpiritType } from '@products/interfaces/spirit-type';

@Component({
  selector: 'app-form-sandbox',
  templateUrl: './form-sandbox.component.html',
  styleUrls: ['./form-sandbox.component.scss'],
})
export class FormSandboxComponent implements OnInit {
  form = new UntypedFormGroup({
    inputExamples: new UntypedFormGroup({
      amount: new UntypedFormControl(null, [
        Validators.required,
        Validators.min(2),
        Validators.max(100),
      ]),
      name: new UntypedFormControl(null, Validators.required),
      country: new UntypedFormControl(null, Validators.required),
      readonly: new UntypedFormControl('Ez egy readonly mező szövege'),
      disabled: new UntypedFormControl('disabled, ez nem módosítható'),
    }),
    selectExamples: new UntypedFormGroup({
      number: new UntypedFormControl(null, Validators.required),
      numberObj: new UntypedFormControl(null, Validators.required),
      numberObjMulti: new UntypedFormControl(null, [
        Validators.required,
        Validators.maxLength(2),
      ]),
      numberObjMultiDisabled: new UntypedFormControl([1, 2]),
      selectOptionsObject: new UntypedFormControl(['option3']),
      drinkType: new UntypedFormControl(null),
      drinkType2: new UntypedFormControl(null),
      merchantType: new UntypedFormControl(null),
    }),
    textarea1: new UntypedFormControl(null),
    textarea2: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(50),
    ]),
    textarea3: new UntypedFormControl('Valami szöveg'),
    checkbox1: new UntypedFormControl(null),
    checkbox2: new UntypedFormControl(true),
    checkbox3: new UntypedFormControl(null),
    checkbox4: new UntypedFormControl(true),
    checkbox5: new UntypedFormControl(null, Validators.requiredTrue),
  });

  numberOptions = ['Egy', 'Kettő', 'Három', 'Négy'];
  numberObjectOptions = [
    { value: '1', name: 'Egy' },
    { value: '2', name: 'Kettő' },
    { value: '3', name: 'Három' },
    { value: '4', name: 'Négy' },
  ];

  merchantTypes = [
    {
      groupName: 'MERCHANT.TYPE_CATEGORIZED.MANUFACTURER_OR_IMPORTER',
      options: [
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.MANUFACTURER_OR_IMPORTER_AS_TAX_WAREHOUSE',
          value: 'MANUFACTURER_OR_IMPORTER_AS_TAX_WAREHOUSE',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.MANUFACTURER_OR_IMPORTER_AS_SIMPLIFIED_TAX_WAREHOUSE',
          value: 'MANUFACTURER_OR_IMPORTER_AS_SIMPLIFIED_TAX_WAREHOUSE',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.MANUFACTURER_OR_IMPORTER_AS_SMALL_SCALE_WINE_PRODUCER',
          value: 'MANUFACTURER_OR_IMPORTER_AS_SMALL_SCALE_WINE_PRODUCER',
        },
      ],
    },
    {
      groupName: 'MERCHANT.TYPE_CATEGORIZED.INTERNATIONAL_TRADER',
      options: [
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.INTERNATIONAL_TRADER_AS_REGISTERED_DEALER',
          value: 'INTERNATIONAL_TRADER_AS_REGISTERED_DEALER',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.INTERNATIONAL_TRADER_AS_COMMUNITY_EXCISE_WHOLESALER',
          value: 'INTERNATIONAL_TRADER_AS_COMMUNITY_EXCISE_WHOLESALER',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.INTERNATIONAL_TRADER_AS_EXPORT_IMPORT_WHOLESALER',
          value: 'INTERNATIONAL_TRADER_AS_EXPORT_IMPORT_WHOLESALER',
        },
      ],
    },
    {
      name: 'MERCHANT.TYPE_CATEGORIZED.WHOLESALER_AS_IS',
      value: 'WHOLESALER_AS_IS',
      highlight: true,
    },
    {
      groupName: 'MERCHANT.TYPE_CATEGORIZED.RETAILER',
      options: [
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.RETAILER_AS_HOTEL',
          value: 'RETAILER_AS_HOTEL',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.RETAILER_AS_RESTAURANT',
          value: 'RETAILER_AS_RESTAURANT',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.RETAILER_AS_CAFE',
          value: 'RETAILER_AS_CAFE',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.RETAILER_AS_RETAIL_CHAIN',
          value: 'RETAILER_AS_RETAIL_CHAIN',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.RETAILER_AS_TRAFIK',
          value: 'RETAILER_AS_TRAFIK',
        },
        {
          name: 'MERCHANT.TYPE_CATEGORIZED.RETAILER_AS_OTHER_RETAILER',
          value: 'RETAILER_AS_OTHER_RETAILER',
        },
      ],
    },
  ];

  drinkTypes = [
    {
      groupName: 'SEARCH.SEARCH_BAR.CATEGORY.ALCOHOLIC_DRINK_TYPE',
      options: Object.values(AlcoholicDrinkType).map((alcType) => {
        switch (alcType) {
          case AlcoholicDrinkType.Spirit:
            return {
              groupName: 'SEARCH.SEARCH_BAR.TITLE.SPIRIT_TYPE',
              value: 'SPIRIT',
              options: Object.values(SpiritType).map((spiritType) => {
                if (spiritType !== SpiritType.Other) {
                  return {
                    name: 'SEARCH.SEARCH_BAR.SPIRIT_TYPE.' + spiritType,
                    value: 'SPIRIT.' + spiritType,
                  };
                } else {
                  return {
                    groupName: 'SEARCH.SEARCH_BAR.TITLE.OTHER_SPIRIT_TYPE',
                    value: 'SPIRIT.OTHER',
                    options: Object.values(OtherSpiritType).map(
                      (otherSpiritType) => ({
                        name:
                          'SEARCH.SEARCH_BAR.OTHER_SPIRIT_TYPE.' +
                          otherSpiritType,
                        value: 'SPIRIT.OTHER.' + otherSpiritType,
                      })
                    ),
                  };
                }
              }),
            };
          case AlcoholicDrinkType.Other:
            return {
              groupName: 'SEARCH.SEARCH_BAR.TITLE.OTHER_ALCOHOL_TYPE',
              value: 'OTHER',
              options: Object.values(OtherAlcoholType).map((otherAlcType) => ({
                name: 'SEARCH.SEARCH_BAR.OTHER_ALCOHOL_TYPE.' + otherAlcType,
                value: 'OTHER.' + otherAlcType,
              })),
            };
          default:
            return {
              name: 'SEARCH.SEARCH_BAR.ALCOHOLIC_DRINK_TYPE.' + alcType,
              value: alcType,
            };
        }
      }),
    },
    {
      groupName: 'SEARCH.SEARCH_BAR.CATEGORY.NON_ALCOHOLIC_DRINK_TYPE',
      options: Object.values(NonAlcoholicDrinkType).map((type) => ({
        name: 'SEARCH.SEARCH_BAR.NON_ALCOHOLIC_DRINK_TYPE.' + type,
        value: type,
      })),
    },
  ];

  ngOnInit() {
    this.getCtrl('inputExamples.disabled').disable();
    this.getCtrl('selectExamples.numberObjMultiDisabled').disable();
    this.getCtrl('textarea3').disable();
    this.getCtrl('checkbox2').disable();
    this.form.valueChanges.subscribe((value) => {
      console.log(value);
      console.log(this.form);
    });

    //this.form.markAllAsTouched();
  }

  getCtrl(controlName: string): UntypedFormControl {
    return this.form.get(controlName) as UntypedFormControl;
  }
}
